/*
  Подвижная кукла: перетаскивание за суставы.

  Перенесено с React-версии макета на чистый JS, чтобы не тащить в тему
  React ради одного блока. Поведение сохранено полностью: обратная
  кинематика на две и три кости, ограничители углов, зеркальные детали,
  сдвиг корпуса с возвратом, покачивание в покое и подгонка под ширину.

  Разметка задаёт всё сама, скрипт ничего не знает про конкретную куклу:
    data-joint="имя"   поворачиваемый узел
    data-min / data-max  пределы поворота в градусах
    data-side="-1|1"     в какую сторону сгибается локоть или колено
    data-pivot           точка вращения, первый потомок узла
    data-grab            зона захвата, только она принимает указатель
    data-body            перетаскивание всей фигуры
    data-mirror="имя"    деталь, повторяющая поворот другого узла

  Благодаря этому одну и ту же куклу можно заменить другой, не трогая код.
*/
(() => {
  "use strict";

  const DEG = 180 / Math.PI;

  class Doll {
    constructor(root, stage, opts) {
      this.root = root;
      this.stage = stage;
      this.opts = opts || {};
      this.J = {};
      this.mirror = {};
      this.angle = {};
      this.target = {};
      this.driven = {};
      this.rest = {};
      this.bodyOff = { x: 0, y: 0 };
      this.bodyTo = { x: 0, y: 0 };

      root.querySelectorAll("[data-joint]").forEach((el) => (this.J[el.dataset.joint] = el));
      root.querySelectorAll("[data-mirror]").forEach((el) => (this.mirror[el.dataset.mirror] = el));

      // Цепочки для обратной кинематики: тянем за кисть - двигается вся рука.
      this.CHAIN = {
        handA: ["armA", "foreA", "handA"], foreA: ["armA", "foreA", "handA"],
        handB: ["armB", "foreB", "handB"], foreB: ["armB", "foreB", "handB"],
        footA: ["legA", "footA"], footB: ["legB", "footB"],
      };

      this.scaleEl = root.firstElementChild;
      this.baseTr = this.scaleEl ? this.scaleEl.style.transform : "";

      // Уважаем системную настройку «меньше движения»: покачивание выключается.
      const calm = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (calm || this.opts.idle === false) {
        Object.values(this.J).forEach((j) => (j.style.animation = "none"));
        Object.values(this.mirror).forEach((m) => (m.style.animation = "none"));
      }

      this.measureRest();
      this.restW = root.getBoundingClientRect().width || 1;
      this.bind();
      this.fit();
      this.loop();
    }

    /* ---- геометрия ---------------------------------------------------- */

    local(e) {
      const c = this.root.getBoundingClientRect();
      return { x: e.clientX - c.left, y: e.clientY - c.top };
    }

    pivotXY(name) {
      const el = this.J[name];
      const p = el.querySelector(":scope > [data-pivot]") || el;
      const r = p.getBoundingClientRect();
      const c = this.root.getBoundingClientRect();
      return { x: r.left + r.width / 2 - c.left, y: r.top + r.height / 2 - c.top };
    }

    /* Точки покоя меряются при нулевом повороте и без анимации, иначе
       кинематика считала бы от качающегося положения. */
    measureRest(names) {
      const list = names || Object.keys(this.J);
      const saved = [];
      list.forEach((n) => {
        const el = this.J[n];
        saved.push([el, el.style.animation, el.style.transform]);
        el.style.animation = "none";
        el.style.transform = "rotate(0deg)";
      });
      void this.root.offsetWidth;
      list.forEach((n) => (this.rest[n] = this.pivotXY(n)));
      saved.forEach(([el, a, tr]) => {
        el.style.animation = a;
        el.style.transform = tr;
      });
      list.forEach((n) => {
        if (this.driven[n]) this.J[n].style.transform = "rotate(" + (this.angle[n] || 0).toFixed(2) + "deg)";
      });
    }

    ancestorsOf(name) {
      const out = [];
      let el = this.J[name].parentElement;
      while (el && el !== this.root) {
        if (el.dataset && el.dataset.joint) {
          const m = getComputedStyle(el).transform;
          const mm = new DOMMatrixReadOnly(m === "none" ? "" : m);
          out.push({ ang: Math.atan2(mm.b, mm.a), piv: this.rest[el.dataset.joint] });
        }
        el = el.parentElement;
      }
      return out.reverse();
    }

    unrotate(T, anc) {
      if (!anc || !anc.length) return T;
      let p = T;
      for (const a of anc) {
        if (!a.piv || !a.ang) continue;
        const c = Math.cos(-a.ang), s = Math.sin(-a.ang);
        const dx = p.x - a.piv.x, dy = p.y - a.piv.y;
        p = { x: a.piv.x + dx * c - dy * s, y: a.piv.y + dx * s + dy * c };
      }
      return p;
    }

    /* Узел переходит под управление указателя: анимация снимается, текущий
       угол берётся из матрицы, чтобы деталь не прыгнула при захвате. */
    take(name) {
      if (this.driven[name]) return;
      const el = this.J[name];
      const m = getComputedStyle(el).transform;
      const mm = new DOMMatrixReadOnly(m === "none" ? "" : m);
      const a = Math.atan2(mm.b, mm.a) * DEG;
      this.angle[name] = a;
      this.target[name] = a;
      el.style.transform = "rotate(" + a.toFixed(2) + "deg)";
      el.style.animation = "none";
      if (this.mirror[name]) {
        this.mirror[name].style.animation = "none";
        this.mirror[name].style.transform = el.style.transform;
      }
      this.driven[name] = true;
    }

    near(name, v) {
      if (!isFinite(v)) return this.target[name] || this.angle[name] || 0;
      const cur = (this.target[name] !== undefined ? this.target[name] : this.angle[name]) || 0;
      const d = (((v - cur) % 360) + 540) % 360 - 180;
      return cur + d;
    }

    clamp(name, v) {
      const el = this.J[name];
      const lo = +el.dataset.min, hi = +el.dataset.max;
      return Math.max(lo, Math.min(hi, v));
    }

    pointerAngle(name, e) {
      const p = this.pivotXY(name);
      const l = this.local(e);
      return Math.atan2(l.y - p.y, l.x - p.x) * DEG;
    }

    solveIK(bones, T, dir) {
      const R = this.rest;
      if (bones.length === 2) {
        const S = R[bones[0]], E = R[bones[1]];
        if (!S || !E) return;
        const rest = Math.atan2(E.y - S.y, E.x - S.x);
        const world = Math.atan2(T.y - S.y, T.x - S.x);
        this.target[bones[0]] = this.clamp(bones[0], this.near(bones[0], (world - rest) * DEG));
        if (dir != null && bones[1]) this.followDirection(bones[1], dir, world);
        return;
      }
      const S = R[bones[0]], E = R[bones[1]], W = R[bones[2]];
      if (!S || !E || !W) return;
      const L1 = Math.hypot(E.x - S.x, E.y - S.y);
      const L2 = Math.hypot(W.x - E.x, W.y - E.y);
      if (!(L1 > 0) || !(L2 > 0)) return;
      const rest1 = Math.atan2(E.y - S.y, E.x - S.x);
      const rest2 = Math.atan2(W.y - E.y, W.x - E.x);
      const fixed = this.J[bones[0]].dataset.side;
      let side;
      if (fixed) side = +fixed;
      else {
        const cross = (E.x - S.x) * (W.y - E.y) - (E.y - S.y) * (W.x - E.x);
        side = cross < 0 ? 1 : -1;
      }
      let d = Math.hypot(T.x - S.x, T.y - S.y);
      const lo = Math.abs(L1 - L2) + Math.max(3, L2 * 0.12);
      const hi = L1 + L2 - Math.max(3, L2 * 0.06);
      d = Math.max(lo, Math.min(hi, d));
      const base = Math.atan2(T.y - S.y, T.x - S.x);
      const a1 = Math.acos(Math.max(-1, Math.min(1, (L1 * L1 + d * d - L2 * L2) / (2 * L1 * d))));
      const w1 = base + side * a1;
      const ex = S.x + Math.cos(w1) * L1, ey = S.y + Math.sin(w1) * L1;
      const w2 = Math.atan2(T.y - ey, T.x - ex);
      const r1 = this.clamp(bones[0], this.near(bones[0], (w1 - rest1) * DEG));
      this.target[bones[0]] = r1;
      this.target[bones[1]] = this.clamp(bones[1], this.near(bones[1], (w2 - rest2) * DEG - r1));
      if (dir != null && bones[2]) this.followDirection(bones[2], dir, w2);
    }

    /* Кисть или ступня доворачивается по направлению движения - небольшой
       доворот делает жест живым, без него конечность выглядит палкой. */
    followDirection(name, dir, world) {
      let off = (dir - world) * DEG;
      off = (((off % 360) + 540) % 360) - 180;
      this.take(name);
      const want = Math.max(-22, Math.min(22, off * 0.2));
      const now = this.target[name] || 0;
      this.target[name] = this.clamp(name, this.near(name, now + (want - now) * 0.25));
    }

    /* ---- ввод ---------------------------------------------------------- */

    bind() {
      let drag = null;

      this.onDown = (e) => {
        const body = e.target.closest("[data-body]");
        if (body && this.root.contains(body)) {
          e.preventDefault();
          body.style.cursor = "grabbing";
          drag = { mode: "body", el: body, from: this.local(e), base: { x: this.bodyOff.x, y: this.bodyOff.y } };
          return;
        }
        const el = e.target.closest("[data-joint]");
        if (!el || !this.root.contains(el)) return;
        e.preventDefault();
        const name = el.dataset.joint;
        const chain = this.CHAIN[name];
        const bones = chain && chain.indexOf(name) > 0 ? chain : null;
        if (bones) bones.forEach((n) => this.take(n));
        else this.take(name);
        el.style.cursor = "grabbing";
        if (bones) {
          const p = this.local(e), tip = this.pivotXY(bones[bones.length - 1]);
          drag = { mode: "ik", bones, el, anc: this.ancestorsOf(bones[0]), off: { x: p.x - tip.x, y: p.y - tip.y } };
        } else {
          drag = { mode: "fk", name, el, grab: this.pointerAngle(name, e), from: this.angle[name] || 0 };
        }
      };

      this.onMove = (e) => {
        if (!drag) return;
        if (drag.mode === "body") {
          const p = this.local(e);
          let x = drag.base.x + (p.x - drag.from.x) * 0.3;
          let y = drag.base.y + (p.y - drag.from.y) * 0.3;
          const r = Math.hypot(x, y), max = 34;
          if (r > max) { x = (x / r) * max; y = (y / r) * max; }
          this.bodyTo = { x, y };
          return;
        }
        if (drag.mode === "ik") {
          const p = this.local(e);
          if (drag.prev) {
            const vx = p.x - drag.prev.x, vy = p.y - drag.prev.y;
            if (Math.hypot(vx, vy) > 1.2) drag.dir = Math.atan2(vy, vx);
          }
          drag.prev = p;
          this.solveIK(drag.bones, this.unrotate({ x: p.x - drag.off.x, y: p.y - drag.off.y }, drag.anc), drag.dir);
          return;
        }
        const a = this.pointerAngle(drag.name, e);
        let d = a - drag.grab;
        d = (((d % 360) + 540) % 360) - 180;
        this.target[drag.name] = this.clamp(drag.name, this.near(drag.name, drag.from + d));
      };

      this.onUp = () => {
        if (!drag) return;
        drag.el.style.cursor = "grab";
        if (drag.mode === "body") this.bodyTo = { x: 0, y: 0 };
        drag = null;
      };

      this.root.addEventListener("pointerdown", this.onDown);
      window.addEventListener("pointermove", this.onMove);
      window.addEventListener("pointerup", this.onUp);
      window.addEventListener("resize", (this.onResize = () => this.resize()));
    }

    /* ---- кадр ---------------------------------------------------------- */

    loop() {
      const tick = () => {
        for (const n in this.driven) {
          if (!this.driven[n]) continue;
          const el = this.J[n];
          const to = this.target[n] || 0;
          const from = this.angle[n] || 0;
          // Кисти и ступни догоняют медленнее - так движение читается мягче.
          const k = n === "handA" || n === "handB" || n === "footA" || n === "footB" ? 0.075 : 0.17;
          const next = Math.abs(to - from) < 0.03 ? to : from + (to - from) * k;
          if (next !== from) {
            this.angle[n] = next;
            const tr = "rotate(" + next.toFixed(2) + "deg)";
            el.style.transform = tr;
            if (this.mirror[n]) this.mirror[n].style.transform = tr;
          }
        }
        const b = this.bodyOff, to = this.bodyTo;
        const dx = to.x - b.x, dy = to.y - b.y;
        if (this.scaleEl && (Math.abs(dx) > 0.02 || Math.abs(dy) > 0.02)) {
          b.x += dx * 0.055;
          b.y += dy * 0.055;
          this.scaleEl.style.transform = "translate(" + b.x.toFixed(2) + "px," + b.y.toFixed(2) + "px) " + this.baseTr;
        }
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    }

    /* Кукла нарисована в 422x632 и ужимается под доступную ширину. Ниже 0.52
       не опускаемся: зоны захвата стали бы меньше пальца. */
    fit() {
      if (!this.stage) return;
      const avail = this.stage.getBoundingClientRect().width - 16;
      const k = Math.max(0.52, Math.min(1, avail / 422));
      if (this.k === k) return;
      this.k = k;
      this.root.style.width = 422 * k + "px";
      this.root.style.height = 632 * k + "px";
      this.root.style.marginLeft = -211 * k + "px";
      if (this.scaleEl) this.scaleEl.style.transform = "scale(" + (0.2346 * k).toFixed(4) + ")";
      this.baseTr = "scale(" + (0.2346 * k).toFixed(4) + ")";
    }

    resize() {
      this.fit();
      const w = this.root.getBoundingClientRect().width;
      if (!this.restW || !w) return;
      const k = w / this.restW;
      if (Math.abs(k - 1) < 0.01) return;
      Object.keys(this.rest).forEach((n) => {
        this.rest[n] = { x: this.rest[n].x * k, y: this.rest[n].y * k };
      });
      this.restW = w;
    }

    reset() {
      Object.keys(this.driven).forEach((n) => {
        this.target[n] = 0;
      });
      this.bodyTo = { x: 0, y: 0 };
    }
  }

  function boot(scope) {
    (scope || document).querySelectorAll("[data-mr-doll]").forEach((stage) => {
      if (stage.dataset.mrDollReady) return;
      const rig = stage.querySelector("[data-mr-rig]");
      if (!rig) return;
      stage.dataset.mrDollReady = "1";
      const doll = new Doll(rig, stage, { idle: stage.dataset.idle !== "off" });
      const btn = stage.parentElement && stage.parentElement.querySelector("[data-mr-reset]");
      if (btn) btn.addEventListener("click", () => doll.reset());
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot());
  } else {
    boot();
  }
  // Редактор темы перерисовывает секцию - поднимаем куклу заново.
  document.addEventListener("shopify:section:load", (e) => boot(e.target));
})();
